import React from 'react';
import { TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  Form,
  Input,
  notification,
  Select,
  Typography,
} from '@duxton-web/ui';

import Service, { UpdateStateResponse } from '../../lib/App';

import './AbortPrompt.css';

import { FormLayout } from '@duxton-web/ui/es/form/Form';

import { formatHasAccessErrorGeneric } from '../../utils/url';
import { HTTP_STATUS } from '@/constants';

const notificationApi = notification;

interface AbortType {
  value: string;
  text: string;
  reasonRequired: boolean;
}

const abortTypes: AbortType[] = [
  {
    value: 'prodIssue',
    text: 'The Experiment caused an issue in Production (increase in CE tickets, MEX Unhappy, Feature not rendering etc)',
    reasonRequired: false,
  },
  {
    value: 'placeholder',
    text: 'I setup this Experiment to block a slot for the actual Experiment that I want to setup',
    reasonRequired: false,
  },
  {
    value: 'accomplished',
    text: 'I am aborting the Experiment since I already have collected required data from it to make my decision',
    reasonRequired: false,
  },
  {
    value: 'wrongConstraints',
    text: 'I picked the wrong constraints/city for the Experiment.',
    reasonRequired: true,
  },
  {
    value: 'notWorking',
    text: 'The Experiment configuration did not work as I expected.',
    reasonRequired: true,
  },
  {
    value: 'facetNotInstrumented',
    text: 'One or more facets used in the experiment were not instrumented.',
    reasonRequired: true,
  },
  {
    value: 'testing',
    text: 'It was a test experiment (tip: Use rollout for testing)',
    reasonRequired: false,
  },
  {
    value: 'other',
    text: 'Other reason',
    reasonRequired: true,
  },
];

interface AbortPromptProps {
  experiment: string;
  isAdaptiveExp?: boolean;
  layout?: FormLayout;
  onCancel?: () => void;
  onSuccess?: () => void;
}

interface AbortPromptState {
  abortType?: string;
  reason?: string;
  abortTypeError?: boolean;
  reasonError?: boolean;
  reasonErrorValue?: string;
}

export class AbortPrompt extends React.Component<
  AbortPromptProps,
  AbortPromptState
> {
  state: AbortPromptState = {};
  constructor(props) {
    super(props);
  }

  abort = async () => {
    if (!this.validate()) {
      return;
    }

    const { abortType, reason } = this.state;
    const { experiment, isAdaptiveExp, onSuccess } = this.props;

    try {
      await Service.planner!.changeExperimentState(
        experiment,
        'cancel',
        `${abortType}:${reason}`,
        isAdaptiveExp,
      ).then((res: UpdateStateResponse) => {
        if ((res && !isAdaptiveExp) || isAdaptiveExp) {
          notificationApi.success({
            message: 'Aborting an Experiment',
            description: 'Experiment was successfully aborted.',
          });
          onSuccess();
        }
      });
    } catch (error) {
      let description = (
        <span>
          Experiment request was not aborted, due to: {error.toString()}
        </span>
      );
      if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(error);
      }
      notificationApi.error({
        description,
        message: 'Aborting an Experiment',
      });
    }
  };

  validate = (): boolean => {
    const { abortType, reason } = this.state;
    if (!abortType) {
      this.setState({
        abortTypeError: true,
      });
      return false;
    }

    const abortTypeObj = abortTypes.find((rt) => rt.value == abortType);
    if (abortTypeObj!.reasonRequired) {
      if (!reason) {
        this.setState({
          reasonError: true,
          reasonErrorValue: 'This field is required',
        });
        return false;
      }
      const cleanReason = reason.trim();
      if (cleanReason.length <= 10) {
        this.setState({
          reasonError: true,
          reasonErrorValue:
            'An ideal reason is usually more than 10 characters long. Please provide a valid reason.',
        });
        return false;
      }
    }
    return true;
  };

  handleAbortType = (value: string) => {
    this.setState({
      abortType: value,
      abortTypeError: false,
    });
  };

  handleReason = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      reason: event.target.value,
      reasonError: false,
    });
  };

  render() {
    const { experiment, onCancel } = this.props;
    const { abortType, reason, abortTypeError, reasonError, reasonErrorValue } =
      this.state;
    const abortTypeObj = abortTypes.find((rt) => rt.value == abortType);

    return (
      <div className="abort-prompt__container">
        <div className="abort-prompt__header">
          <Typography.Text strong type="danger">
            {experiment}
          </Typography.Text>
        </div>
        <Form layout={this.props.layout}>
          <Form.Item
            label="Select reason why you are aborting this experiment?"
            required
            validateStatus={abortTypeError ? 'error' : ''}
            help={abortTypeError ? 'This field is required' : ''}
          >
            <Select value={abortType} onChange={this.handleAbortType}>
              {abortTypes.map((rt) => (
                <Select.Option key={rt.value} value={rt.value}>
                  {rt.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Additional reasons. Please provide a valid reason to help us accelerate Experimentation at Grab."
            required={abortTypeObj && abortTypeObj.reasonRequired}
            validateStatus={reasonError ? 'error' : ''}
            help={reasonError ? reasonErrorValue : ''}
          >
            <Input.TextArea
              rows={8}
              value={reason}
              onChange={this.handleReason}
            />
          </Form.Item>
          <Form.Item>
            {onCancel && (
              <Button size="small" onClick={onCancel}>
                Back
              </Button>
            )}
            <Button
              size="small"
              danger
              icon={<TrashCanOutlined />}
              className="abort-prompt__abort-button"
              onClick={this.abort}
            >
              Abort
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }
}

export default AbortPrompt;
